// Mock data for Dard Satya Palliative Care Website

export const services = [
  {
    id: 1,
    title: "Cancer Pain Management",
    description: "Specialized care for managing chronic pain associated with cancer. Our comprehensive approach combines medical interventions with holistic support.",
    icon: "heart-pulse",
    image: "https://images.pexels.com/photos/8413336/pexels-photo-8413336.jpeg",
    details: {
      overview: "Cancer pain can significantly impact quality of life. Our expert team provides personalized pain management strategies to help patients live more comfortably.",
      approaches: [
        "Medication management and optimization",
        "Interventional pain procedures",
        "Complementary therapies",
        "Psychological support",
        "24/7 pain crisis management"
      ],
      conditions: ["Bone pain", "Neuropathic pain", "Visceral pain", "Post-surgical pain", "Treatment-related pain"]
    }
  },
  {
    id: 2,
    title: "Neuro Palliative Care",
    description: "Specialized support for patients with progressive neurological conditions, focusing on comfort and quality of life.",
    icon: "brain",
    image: "https://images.pexels.com/photos/8413300/pexels-photo-8413300.jpeg",
    details: {
      overview: "Neurological conditions require specialized palliative care approaches. We provide comprehensive support tailored to each patient's unique needs.",
      approaches: [
        "Symptom management for neurological conditions",
        "Communication support",
        "Mobility and comfort care",
        "Cognitive support strategies",
        "Family counseling and education"
      ],
      conditions: ["Dementia", "Parkinson's disease", "ALS", "Multiple sclerosis", "Brain tumors"]
    }
  },
  {
    id: 3,
    title: "Stroke & Brain Injury Care",
    description: "Compassionate care for patients recovering from stroke, traumatic brain injury, and related conditions.",
    icon: "activity",
    image: "https://images.unsplash.com/photo-1631217868204-db1ed6bdd224",
    details: {
      overview: "Post-stroke and brain injury care requires specialized attention. We provide holistic support for patients and their families during recovery and long-term care.",
      approaches: [
        "Post-acute care management",
        "Rehabilitation support",
        "Spasticity management",
        "Nutritional support",
        "Caregiver training and support"
      ],
      conditions: ["Ischemic stroke", "Hemorrhagic stroke", "Traumatic brain injury", "Hypoxic brain injury", "Post-coma care"]
    }
  },
  {
    id: 4,
    title: "Coma & Consciousness Care",
    description: "Specialized care for patients with altered consciousness, providing dignity and comfort during critical times.",
    icon: "bed",
    image: "https://customer-assets.emergentagent.com/job_palliative-portal/artifacts/opuo0jcf_coma.jpg",
    details: {
      overview: "Caring for patients with altered consciousness requires expertise and compassion. We support families through these challenging times with comprehensive care plans.",
      approaches: [
        "Comfort care protocols",
        "Family communication and support",
        "Dignity-centered care",
        "Medical monitoring",
        "End-of-life planning support"
      ],
      conditions: ["Coma", "Vegetative state", "Minimally conscious state", "Brain death", "Severe consciousness disorders"]
    }
  },
  {
    id: 5,
    title: "Dementia Care",
    description: "Person-centered care for individuals living with dementia, supporting both patients and their families.",
    icon: "users",
    image: "https://images.unsplash.com/photo-1761234852163-23f2ededd04e",
    details: {
      overview: "Dementia care requires patience, understanding, and specialized approaches. We provide comprehensive support throughout the journey.",
      approaches: [
        "Behavioral symptom management",
        "Memory care strategies",
        "Communication techniques",
        "Caregiver education and respite",
        "End-stage dementia care"
      ],
      conditions: ["Alzheimer's disease", "Vascular dementia", "Lewy body dementia", "Frontotemporal dementia", "Mixed dementia"]
    }
  },
  {
    id: 6,
    title: "Hospice Care",
    description: "Compassionate end-of-life care focused on comfort, dignity, and quality of life for patients and their loved ones.",
    icon: "home",
    image: "https://images.pexels.com/photos/5206923/pexels-photo-5206923.jpeg",
    details: {
      overview: "Hospice care honors life while providing comfort in the final stages. Our team supports patients and families with compassion and dignity.",
      approaches: [
        "Symptom and pain management",
        "Emotional and spiritual support",
        "Respite care for families",
        "Bereavement counseling",
        "24/7 on-call support"
      ],
      conditions: ["Terminal cancer", "End-stage organ failure", "Advanced neurological disease", "Multiple chronic conditions", "Life-limiting illnesses"]
    }
  }
];

export const testimonials = [
  {
    id: 1,
    name: "Rajesh Kumar",
    relation: "Son of patient",
    text: "Dard Satya gave my father the comfort and dignity he deserved in his final days. The team's compassion made an unbearable time bearable.",
    rating: 5
  },
  {
    id: 2,
    name: "Priya Sharma",
    relation: "Daughter caring for mother",
    text: "When my mother was diagnosed with advanced dementia, I felt lost. Dard Satya not only helped her but taught us how to care with love and patience.",
    rating: 5
  },
  {
    id: 3,
    name: "Amit Patel",
    relation: "Cancer patient",
    text: "Living with cancer pain was affecting every aspect of my life. The pain management support I received has given me quality of life I thought was impossible.",
    rating: 5
  },
  {
    id: 4,
    name: "Sunita Verma",
    relation: "Wife of stroke patient",
    text: "After my husband's stroke, we didn't know where to turn. Dard Satya provided not just medical care but emotional support for our entire family.",
    rating: 5
  }
];

export const faqs = [
  {
    id: 1,
    question: "What is palliative care?",
    answer: "Palliative care is specialized medical care focused on providing relief from pain and other symptoms of serious illness. It aims to improve quality of life for both patients and families, regardless of the stage of disease or need for other therapies."
  },
  {
    id: 2,
    question: "Is palliative care only for cancer patients?",
    answer: "No, palliative care is for anyone with a serious illness including cancer, heart disease, lung disease, kidney failure, dementia, neurological conditions like stroke or brain injury, and other life-limiting conditions."
  },
  {
    id: 3,
    question: "What is the difference between palliative care and hospice care?",
    answer: "Palliative care can be provided at any stage of serious illness alongside curative treatment. Hospice care is a type of palliative care specifically for patients in the final stages of life, typically when curative treatment is no longer pursued."
  },
  {
    id: 4,
    question: "How do I know if my loved one needs palliative care?",
    answer: "Consider palliative care if your loved one has a serious illness causing pain or distressing symptoms, faces difficult treatment decisions, needs help coordinating care, or would benefit from additional support for quality of life."
  },
  {
    id: 5,
    question: "Do you provide home-based care?",
    answer: "Yes, we provide consultation and guidance for home-based palliative care. We work with families to create comfortable care environments and provide 24/7 telephonic support for urgent concerns."
  },
  {
    id: 6,
    question: "How can I schedule a consultation?",
    answer: "You can contact us via phone at 9123647588, WhatsApp, or through our website contact form. We offer initial consultations to discuss your needs and how we can help."
  },
  {
    id: 7,
    question: "What services do you provide for brain injury patients?",
    answer: "We provide comprehensive care for traumatic brain injury, stroke, coma, and other consciousness disorders. This includes symptom management, family support, rehabilitation guidance, and long-term care planning."
  },
  {
    id: 8,
    question: "Do you offer support for caregivers?",
    answer: "Absolutely. We recognize that caregivers need support too. We offer caregiver education, counseling, respite planning, and emotional support to help families through their caregiving journey."
  }
];

export const teamMembers = [
  {
    id: 1,
    name: "Dr. [Name]",
    role: "Palliative Care Specialist",
    qualifications: "MD, Palliative Medicine",
    description: "Specialized in pain management and end-of-life care with over 15 years of experience.",
    image: "https://images.pexels.com/photos/5452293/pexels-photo-5452293.jpeg"
  },
  {
    id: 2,
    name: "Dr. [Name]",
    role: "Neurologist",
    qualifications: "MD, DM Neurology",
    description: "Expert in neuro-palliative care for stroke, dementia, and brain injury patients.",
    image: "https://images.pexels.com/photos/5327585/pexels-photo-5327585.jpeg"
  },
  {
    id: 3,
    name: "[Name]",
    role: "Clinical Psychologist",
    qualifications: "PhD, Clinical Psychology",
    description: "Providing psychological support for patients and families facing serious illness.",
    image: "https://images.pexels.com/photos/5452201/pexels-photo-5452201.jpeg"
  }
];

export const appointmentTypes = [
  { value: "consultation", label: "Initial Consultation" },
  { value: "follow-up", label: "Follow-up Appointment" },
  { value: "pain-management", label: "Pain Management" },
  { value: "family-counseling", label: "Family Counseling" },
  { value: "home-visit", label: "Home Visit Request" },
  { value: "emergency", label: "Urgent Care" }
];

export const contactInfo = {
  phone: "9244093291",
  whatsapp: "919244093291",
  facebook: "",
  instagram: "",
  email: "care@dardsatya.com",
  slogan: "Where comfort meets care"
};
